import { createStore } from 'react-sweet-state';
import { cloneDeep } from 'lodash';

import { convertExtractedAnnotations, filterAnnotationsByTool } from 'src/data/annotation';
import { mapToolContentForBriefInput } from 'src/data/brief';
import { getRewriteHeadlineTools, getToolMaxWordsPerKeyword } from 'src/data/generationTool';
import { extractBriefAnnotations, extractCopyAnnotations } from 'src/graphql/annotation';
import { isAbortError } from 'src/lib/errors';

const INITIAL_STATE = {
  suggestedAnnotations: [],
  suggestedAnnotationsParamsHash: {},
  loadingSuggestions: false,
  requestedSuggestions: false,
  copyAnnotationsAbortController: null,
};
const MAX_WORDS_PER_KEYWORD_HEADLINE = 2;
const REWRITE_HEADLINE_TOOLS = getRewriteHeadlineTools();

export const PRIVATE_ACTIONS = {
  init:
    () =>
    ({ setState }) => {
      setState({
        ...cloneDeep(INITIAL_STATE),
      });
    },
};

export const ACTIONS = {
  fetchSuggestedAnnotations:
    (params) =>
    async ({ setState, dispatch }, { generationTool, customer }) => {
      try {
        if (!generationTool || !params.briefContent) {
          return;
        }
        const brief = mapToolContentForBriefInput(generationTool, params.briefContent);
        const maxWords = getToolMaxWordsPerKeyword(generationTool);

        const result = await extractBriefAnnotations({
          customerId: customer?.id,
          generationTool,
          maxWords,
          brief,
          promptImageId: params.promptImage?.id,
          promptDocumentId: params.promptDocument?.id,
          freestyleToolAllInstructionsData: params.freestyleToolAllInstructionsData,
          urlExtractions: params.urlExtractions,
        });

        const converted = convertExtractedAnnotations(result);
        const filtered = filterAnnotationsByTool({ annotations: converted, generationTool });
        dispatch(ACTIONS.setSuggestedAnnotations(filtered));
      } finally {
        setState({ loadingSuggestions: false });
      }
    },

  fetchSuggestedCopyAnnotations:
    (text, rewriteProductName) =>
    async ({ setState, dispatch }, { generationTool, customer }) => {
      if (!generationTool || text === '') {
        return;
      }
      const maxWordsPerKeyword = REWRITE_HEADLINE_TOOLS.includes(generationTool)
        ? MAX_WORDS_PER_KEYWORD_HEADLINE
        : getToolMaxWordsPerKeyword(generationTool);

      const abortController = new AbortController();
      setState({ copyAnnotationsAbortController: abortController });

      try {
        const result = await extractCopyAnnotations(
          {
            customerId: customer?.id,
            generationTool,
            text,
            productName: rewriteProductName,
            maxWordsPerKeyword,
          },
          { abortSignal: abortController.signal }
        );

        const converted = convertExtractedAnnotations(result);
        const filtered = filterAnnotationsByTool({ annotations: converted, generationTool });
        dispatch(ACTIONS.setSuggestedAnnotations(filtered));
      } catch (error) {
        if (!isAbortError(error)) {
          throw error;
        }
      } finally {
        setState({ loadingSuggestions: false });
      }
    },

  cancelCopyAnnotationsRequest:
    () =>
    ({ getState, setState }) => {
      const { copyAnnotationsAbortController } = getState();
      if (copyAnnotationsAbortController) {
        copyAnnotationsAbortController.abort();
        setState({ copyAnnotationsAbortController: null });
      }
    },

  setSuggestedAnnotations:
    (annotations) =>
    ({ setState }) => {
      const sorted = annotations.sort((a, b) => b.score - a.score);
      setState({ suggestedAnnotations: sorted });
    },

  setLoadingSuggestions:
    (loading) =>
    ({ setState }) => {
      setState({ loadingSuggestions: loading });
    },

  clearSuggestedAnnotations:
    () =>
    ({ setState }) => {
      setState({
        suggestedAnnotations: [],
        suggestedAnnotationsParamsHash: {},
        requestedSuggestions: false,
      });
    },
};

export const AnnotationStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'popover-annotations',
});
